/**
 * Clasificación de cuentas: dónde va cada cuenta que el mapping no cubría.
 *
 * Cuando aparece en Odoo una cuenta sin renglón, la sincronización la aísla en
 * «Pendiente de clasificar» y los estados siguen cuadrando; lo único que se
 * bloquea son las descargas oficiales de los períodos que mueve. Aquí el
 * contador decide, y cada decisión queda como una revisión con autor, fecha y
 * motivo que ninguna otra borra: solo la reemplaza.
 *
 * El formulario se ofrece con `mapping:write`, que tiene solo el contador.
 * Ocultarlo es comodidad: el servidor lo exige igual.
 */

import { useMemo, useState } from "react";
import { api } from "../api/client";
import { mayApproveClassification } from "../api/permissions";
import type {
  ClassificationRequest,
  ClassificationRevision,
  ClassificationScreen,
  ClassifiedAccount,
  ProposalCandidate,
} from "../api/types";
import { useAsync } from "../api/useAsync";
import { ErrorState, Loading } from "../components/States";
import { StatusBadge } from "../components/StatusBadge";
import type { StatusTone } from "../components/tones";
import { formatDateTime, formatMoney, formatMonth, moneyClass } from "../components/format";
import { useAudit } from "./auditContext";

const ESTADOS: Record<ClassifiedAccount["status"], { texto: string; tono: StatusTone }> = {
  provisional: { texto: "Pendiente de clasificar", tono: "serious" },
  revision_inapplicable: { texto: "Decisión que ya no aplica", tono: "serious" },
  unresolved: { texto: "Sin sección: bloquea", tono: "critical" },
  pending_no_activity: { texto: "Nueva, sin movimiento", tono: "neutral" },
  absorbed_new: { texto: "Nueva, absorbida por prefijo", tono: "warning" },
  approved: { texto: "Aprobada", tono: "good" },
  allowlisted: { texto: "Excluida (allowlist)", tono: "neutral" },
  mapped: { texto: "Cubierta por el mapping", tono: "good" },
};

const ACTIVIDADES = [
  { id: "operating", texto: "Operación" },
  { id: "investing", texto: "Inversión" },
  { id: "financing", texto: "Financiamiento" },
] as const;

export function AuditClassification() {
  const { session } = useAudit();
  const [recarga, setRecarga] = useState(0);
  const estado = useAsync(() => api.classifications(), [recarga]);

  if (estado.loading) return <Loading what="la clasificación de cuentas" />;
  if (estado.error) return <ErrorState error={estado.error} />;
  if (!estado.data) return null;

  const pantalla = estado.data;
  const puedeAprobar = pantalla.can_approve && mayApproveClassification(session);
  const bloquean = pantalla.accounts.filter(
    (c) => c.isolated || c.status === "unresolved" || c.status === "revision_inapplicable",
  );
  const revisar = pantalla.accounts.filter((c) => !bloquean.includes(c));

  return (
    <>
      <div className="card">
        <h2>Pendientes de clasificar</h2>
        <p className="small muted">
          Cuentas con movimiento que ningún renglón cubre. Sus cifras están en
          «Pendiente de clasificar» y las descargas oficiales del paquete de cierre de
          los períodos que mueven están bloqueadas hasta que se decida.
        </p>
        <TablaDeCuentas
          cuentas={bloquean}
          pantalla={pantalla}
          puedeAprobar={puedeAprobar}
          vacio="Ninguna cuenta pendiente."
          alGuardar={() => setRecarga((n) => n + 1)}
        />
      </div>

      <div className="card">
        <h2>Nuevas absorbidas y conflictos</h2>
        <p className="small muted">
          Entraron en un renglón existente por su código, o su tipo en Odoo contradice
          el renglón. No bloquean nada; confirmarlas las saca de esta lista.
        </p>
        <TablaDeCuentas
          cuentas={revisar}
          pantalla={pantalla}
          puedeAprobar={puedeAprobar}
          vacio="Nada que revisar."
          alGuardar={() => setRecarga((n) => n + 1)}
        />
      </div>

      <Historial revisiones={pantalla.history} />
    </>
  );
}

function TablaDeCuentas({
  cuentas,
  pantalla,
  puedeAprobar,
  vacio,
  alGuardar,
}: {
  cuentas: ClassifiedAccount[];
  pantalla: ClassificationScreen;
  puedeAprobar: boolean;
  vacio: string;
  alGuardar: () => void;
}) {
  const [abierta, setAbierta] = useState<number | null>(null);
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th scope="col">Cuenta</th>
            <th scope="col">Tipo en Odoo</th>
            <th scope="col">Estado</th>
            <th scope="col">Renglón</th>
            <th scope="col">Movimiento</th>
            <th scope="col" className="num">
              Saldo
            </th>
            <th scope="col">Propuesta</th>
            {puedeAprobar && <th scope="col">Decisión</th>}
          </tr>
        </thead>
        <tbody>
          {cuentas.length === 0 && (
            <tr>
              <td colSpan={puedeAprobar ? 8 : 7} className="muted">
                {vacio}
              </td>
            </tr>
          )}
          {cuentas.map((cuenta) => (
            <FilaDeCuenta
              key={cuenta.account_id}
              cuenta={cuenta}
              pantalla={pantalla}
              puedeAprobar={puedeAprobar}
              abierta={abierta === cuenta.account_id}
              alternar={() =>
                setAbierta(abierta === cuenta.account_id ? null : cuenta.account_id)
              }
              alGuardar={() => {
                setAbierta(null);
                alGuardar();
              }}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FilaDeCuenta({
  cuenta,
  pantalla,
  puedeAprobar,
  abierta,
  alternar,
  alGuardar,
}: {
  cuenta: ClassifiedAccount;
  pantalla: ClassificationScreen;
  puedeAprobar: boolean;
  abierta: boolean;
  alternar: () => void;
  alGuardar: () => void;
}) {
  const estado = ESTADOS[cuenta.status];
  const candidatos = leerCandidatos(cuenta);
  return (
    <>
      <tr>
        <td>
          <span className="mono">{cuenta.account_code}</span> {cuenta.account_name}
        </td>
        <td className="mono small">{cuenta.account_type ?? "—"}</td>
        <td>
          <StatusBadge tone={estado.tono}>{estado.texto}</StatusBadge>
          {(cuenta.conflicts ?? []).map((conflicto) => (
            <div className="small muted" key={conflicto}>
              {conflicto}
            </div>
          ))}
        </td>
        <td className="mono small">
          {cuenta.line_id ?? "—"}
          {cuenta.presentation_pending && (
            <div className="small muted">
              Pendiente en {Object.keys(JSON.parse(cuenta.presentation_pending)).join(", ").toUpperCase()}
            </div>
          )}
        </td>
        <td className="small">
          {cuenta.first_month
            ? `${formatMonth(cuenta.first_month)} – ${formatMonth(cuenta.last_month)}`
            : "sin movimiento"}
        </td>
        <td className={moneyClass(cuenta.balance)}>{formatMoney(cuenta.balance)}</td>
        <td className="small">
          {candidatos.candidatos.length === 0 && (
            <span className="muted">{candidatos.nota || "—"}</span>
          )}
          {candidatos.candidatos.map((c) => (
            <div key={c.line_id}>
              <span className="mono">{c.line_id}</span> {c.label}
              <div className="muted">
                {c.evidence}
                {c.nature_conflict ? " (contradice el tipo)" : ""}
              </div>
            </div>
          ))}
        </td>
        {puedeAprobar && (
          <td>
            <button onClick={alternar} aria-expanded={abierta}>
              {abierta ? "Cerrar" : "Decidir"}
            </button>
          </td>
        )}
      </tr>
      {abierta && puedeAprobar && (
        <tr>
          <td colSpan={8}>
            <Formulario
              cuenta={cuenta}
              pantalla={pantalla}
              sugerida={candidatos.candidatos[0]?.line_id}
              alGuardar={alGuardar}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function Formulario({
  cuenta,
  pantalla,
  sugerida,
  alGuardar,
}: {
  cuenta: ClassifiedAccount;
  pantalla: ClassificationScreen;
  sugerida?: string;
  alGuardar: () => void;
}) {
  const cabeza = useMemo(
    () =>
      pantalla.history
        .filter((r) => r.odoo_account_id === cuenta.account_id)
        .reduce<number | null>((max, r) => (max === null || r.revision > max ? r.revision : max), null),
    [pantalla.history, cuenta.account_id],
  );
  const [renglon, setRenglon] = useState(sugerida ?? "");
  const [eri, setEri] = useState("");
  const [esf, setEsf] = useState("");
  const [flujo, setFlujo] = useState("");
  const [componente, setComponente] = useState("");
  const [naturaleza, setNaturaleza] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const absorbida = cuenta.status === "absorbed_new" || cuenta.status === "mapped";

  async function enviar(accion: ClassificationRequest["action"]) {
    setEnviando(true);
    setError(null);
    const lineas: Record<string, string> = {};
    if (eri) lineas.eri = eri;
    if (esf) lineas.esf = esf;
    try {
      await api.approveClassification({
        account_id: cuenta.account_id,
        action: accion,
        target_line: accion === "classify" ? renglon : null,
        presentation_lines: accion === "classify" ? lineas : {},
        cash_flow_activity:
          accion === "classify" && flujo
            ? (flujo as ClassificationRequest["cash_flow_activity"])
            : null,
        equity_component: accion === "classify" && componente ? componente : null,
        nature_override: accion === "classify" && naturaleza,
        reason: motivo,
        supersedes: cabeza,
      });
      alGuardar();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setEnviando(false);
    }
  }

  const motivoValido = motivo.trim().length >= 10;
  return (
    <div className="formulario-clasificacion">
      <div className="field">
        <label htmlFor={`renglon-${cuenta.account_id}`}>Renglón del estado</label>
        <select
          id={`renglon-${cuenta.account_id}`}
          value={renglon}
          onChange={(e) => setRenglon(e.target.value)}
        >
          <option value="">— Elija —</option>
          {["statement_financial_position", "income_statement"].map((estado) => (
            <optgroup
              key={estado}
              label={estado === "income_statement" ? "Estado de Resultados" : "Estado de Situación"}
            >
              {pantalla.leaves
                .filter((hoja) => hoja.statement === estado)
                .map((hoja) => (
                  <option key={hoja.id} value={hoja.id}>
                    {hoja.id} · {hoja.label}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </div>
      <SelectorDeCapa2
        id={`eri-${cuenta.account_id}`}
        etiqueta="Renglón del ERI (solo si la hoja está desglosada)"
        opciones={pantalla.presentation_lines.eri ?? []}
        valor={eri}
        cambiar={setEri}
      />
      <SelectorDeCapa2
        id={`esf-${cuenta.account_id}`}
        etiqueta="Renglón del ESF (solo si la hoja está desglosada)"
        opciones={pantalla.presentation_lines.esf ?? []}
        valor={esf}
        cambiar={setEsf}
      />
      <div className="field">
        <label htmlFor={`flujo-${cuenta.account_id}`}>Actividad en el flujo de efectivo</label>
        <select id={`flujo-${cuenta.account_id}`} value={flujo} onChange={(e) => setFlujo(e.target.value)}>
          <option value="">— La que ya cubre su código —</option>
          {ACTIVIDADES.map((a) => (
            <option key={a.id} value={a.id}>
              {a.texto}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor={`ecp-${cuenta.account_id}`}>Componente del patrimonio (solo patrimonio)</label>
        <select id={`ecp-${cuenta.account_id}`} value={componente} onChange={(e) => setComponente(e.target.value)}>
          <option value="">— Ninguno —</option>
          {pantalla.equity_components.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>
          <input type="checkbox" checked={naturaleza} onChange={(e) => setNaturaleza(e.target.checked)} />{" "}
          El tipo de la cuenta en Odoo contradice este renglón y la clasificación es correcta igual
        </label>
      </div>
      <div className="field">
        <label htmlFor={`motivo-${cuenta.account_id}`}>Motivo (queda en el historial)</label>
        <textarea
          id={`motivo-${cuenta.account_id}`}
          rows={2}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Por qué va aquí: acta, instrucción o criterio contable"
        />
      </div>
      {error && (
        <p className="small" role="alert">
          <StatusBadge tone="critical">No se guardó</StatusBadge> {error}
        </p>
      )}
      <div className="toolbar">
        <button
          className="primary"
          disabled={enviando || !renglon || !motivoValido}
          onClick={() => enviar("classify")}
        >
          Aprobar clasificación
        </button>
        {absorbida && (
          <button disabled={enviando || !motivoValido} onClick={() => enviar("confirm")}>
            Confirmar donde está
          </button>
        )}
        {cabeza !== null && (
          <button disabled={enviando || !motivoValido} onClick={() => enviar("revoke")}>
            Revocar la decisión vigente
          </button>
        )}
        {!motivoValido && <span className="small muted">El motivo necesita al menos 10 caracteres.</span>}
      </div>
    </div>
  );
}

function SelectorDeCapa2({
  id,
  etiqueta,
  opciones,
  valor,
  cambiar,
}: {
  id: string;
  etiqueta: string;
  opciones: { id: string; label: string }[];
  valor: string;
  cambiar: (v: string) => void;
}) {
  if (opciones.length === 0) return null;
  return (
    <div className="field">
      <label htmlFor={id}>{etiqueta}</label>
      <select id={id} value={valor} onChange={(e) => cambiar(e.target.value)}>
        <option value="">— No hace falta —</option>
        {opciones.map((o) => (
          <option key={o.id} value={o.id}>
            {o.id} · {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Historial({ revisiones }: { revisiones: ClassificationRevision[] }) {
  const acciones = { classify: "Clasificó", confirm: "Confirmó", revoke: "Revocó" } as const;
  return (
    <div className="card">
      <h2>Historial de decisiones</h2>
      <p className="small muted">
        Cada decisión es una revisión que no se modifica: una nueva la reemplaza. La
        corrida registra la revisión con la que se construyó.
      </p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Rev.</th>
              <th scope="col">Cuenta</th>
              <th scope="col">Acción</th>
              <th scope="col">Renglón</th>
              <th scope="col">Motivo</th>
              <th scope="col">Quién y cuándo</th>
            </tr>
          </thead>
          <tbody>
            {revisiones.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  Todavía no hay decisiones registradas.
                </td>
              </tr>
            )}
            {revisiones.map((r) => (
              <tr key={r.revision}>
                <td className="mono">
                  {r.revision}
                  {r.supersedes !== null && <div className="small muted">reemplaza {r.supersedes}</div>}
                </td>
                <td className="mono">{r.account_code}</td>
                <td>{acciones[r.action]}</td>
                <td className="mono small">
                  {r.target_line ?? "—"}
                  {r.nature_override && <div className="small muted">contra el tipo, reconocido</div>}
                </td>
                <td className="small">{r.reason}</td>
                <td className="small">
                  {r.approved_by}
                  <div className="muted">{formatDateTime(r.approved_at)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function leerCandidatos(cuenta: ClassifiedAccount): { candidatos: ProposalCandidate[]; nota: string } {
  if (!cuenta.proposals) return { candidatos: [], nota: "" };
  try {
    const propuesta = JSON.parse(cuenta.proposals) as { candidates?: ProposalCandidate[]; note?: string };
    return { candidatos: propuesta.candidates ?? [], nota: propuesta.note ?? "" };
  } catch {
    return { candidatos: [], nota: "" };
  }
}
